import { useState, useRef, useEffect, useCallback } from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspaceRoles } from "@/hooks/useWorkspaceRoles";
import CreateWorkspaceModal from "./CreateWorkspaceModal";
import AssignWorkspaceMembersModal from "./AssignWorkspaceMembersModal";
import WorkspaceIcon from "./WorkspaceIcon";

/**
 * @param {{ wide: boolean }} props
 */
export default function WorkspaceSwitcher({ wide }) {
  const { user } = useAuthContext();
  const { workspaces, activeWorkspace, loading, switchWorkspace, createWorkspace, assignWorkspaceMembers } = useWorkspace();
  const { isAdmin, loading: rolesLoading } = useWorkspaceRoles();
  const canCreateWorkspace = isAdmin && !rolesLoading;
  const canAssignWorkspaceMembers = isAdmin && !rolesLoading;
  const [menuOpen, setMenuOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const wrapRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(/** @type {Node} */ (e.target))) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const handlePick = useCallback(
    async (id) => {
      await switchWorkspace(id);
      setMenuOpen(false);
    },
    [switchWorkspace]
  );

  if (!user?.email) {
    return wide ? (
      <span className="ml-2 truncate text-sm font-semibold tracking-tight text-slate-800 dark:text-slate-100">Workspace</span>
    ) : null;
  }

  const label = loading ? "Loading…" : activeWorkspace?.name ?? "Workspace";
  const bu = activeWorkspace?.businessUnit;

  const triggerClass = wide
    ? "ml-1 flex min-w-0 flex-1 items-center gap-1 rounded-xl py-1.5 pl-1 pr-1 text-left transition-colors hover:bg-slate-100/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:hover:bg-slate-800/80"
    : "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-600 transition-colors hover:bg-slate-100 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-teal-300";

  return (
    <>
      <div className={`relative ${wide ? "min-w-0 flex-1" : "shrink-0"}`} ref={wrapRef}>
        <button
          type="button"
          className={triggerClass}
          aria-expanded={menuOpen}
          aria-haspopup="listbox"
          aria-label={`Current workspace: ${label}. Change workspace.`}
          title={wide ? undefined : label}
          onClick={() => setMenuOpen((o) => !o)}
        >
          {wide ? (
            <>
              <WorkspaceIcon
                name={label}
                logoUrl={activeWorkspace?.logoUrl}
                size="sm"
                className="shrink-0"
              />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight text-slate-800 dark:text-slate-100">{label}</span>
              {bu ? (
                <span className="hidden max-w-[5rem] truncate rounded-md bg-slate-200/90 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700 ring-1 ring-slate-300 dark:bg-slate-800/90 dark:text-teal-200/90 dark:ring-slate-600 sm:inline">
                  {bu}
                </span>
              ) : null}
              <i
                className={`ri-arrow-down-s-line shrink-0 text-lg text-slate-500 transition-transform dark:text-slate-400 ${menuOpen ? "rotate-180" : ""}`}
                aria-hidden
              />
            </>
          ) : (
            <WorkspaceIcon name={label} logoUrl={activeWorkspace?.logoUrl} size="md" shape="circle" />
          )}
        </button>

        {menuOpen && (
          <div
            className="absolute left-0 top-full z-[70] mt-1.5 w-[min(100vw-2rem,18rem)] rounded-2xl border border-slate-200/90 bg-white py-1.5 shadow-card-hover ring-1 ring-slate-900/[0.04] dark:border-slate-600 dark:bg-slate-900 dark:ring-white/5"
            role="listbox"
            aria-label="Workspaces"
          >
            <p className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Your workspaces</p>
            <div className="max-h-64 overflow-y-auto scrollbar-thin">
              {workspaces.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  role="option"
                  aria-selected={w.id === activeWorkspace?.id}
                  className={`flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/80 ${
                    w.id === activeWorkspace?.id ? "bg-teal-50/80 text-teal-950 dark:bg-teal-950/40 dark:text-teal-50" : "text-slate-800 dark:text-slate-200"
                  }`}
                  onClick={() => void handlePick(w.id)}
                >
                  <div className="flex w-full items-center gap-2.5">
                    <WorkspaceIcon name={w.name} logoUrl={w.logoUrl} size="sm" />
                    <div className="min-w-0 flex-1">
                      <span className="block font-semibold">{w.name}</span>
                      {w.businessUnit ? <span className="text-xs text-slate-500 dark:text-slate-400">{w.businessUnit}</span> : null}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-1 border-t border-slate-100 pt-1 dark:border-slate-700">
              {canAssignWorkspaceMembers ? (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800/80"
                  onClick={() => {
                    setMenuOpen(false);
                    setAssignOpen(true);
                  }}
                >
                  <i className="ri-user-shared-line text-lg text-slate-500 dark:text-slate-400" aria-hidden />
                  Assign users…
                </button>
              ) : null}
              {canCreateWorkspace ? (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-teal-700 hover:bg-teal-50/60 dark:text-teal-300 dark:hover:bg-teal-950/40"
                  onClick={() => {
                    setMenuOpen(false);
                    setCreateOpen(true);
                  }}
                >
                  <i className="ri-add-circle-line text-lg" aria-hidden />
                  New workspace
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <CreateWorkspaceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={async (payload) => {
          await createWorkspace(payload);
        }}
      />

      <AssignWorkspaceMembersModal
        open={assignOpen}
        workspaceId={activeWorkspace?.id ?? null}
        workspaceName={activeWorkspace?.name}
        onClose={() => setAssignOpen(false)}
        onSave={async (emails) => {
          if (!activeWorkspace?.id) return;
          await assignWorkspaceMembers({ workspaceId: activeWorkspace.id, userEmails: emails });
        }}
      />
    </>
  );
}
